import { useUiTranslator } from "../../hooks/useUiTranslator";
import {
  useGetApiEasyAdministrationArea,
  useGetApiLineItemApiLineItemProperties,
  usePutApiLineItemApiLineItemPropertiesWithJson,
} from "../../openapi/backendApi";
import { useEasySession } from "../../providers/easySessionProvider";
import CopyToClipboard from "../CopyToClipboard/CopyToClipboard";
import { ErrorType } from "../CustomCartIdRouteComponent/CustomCartIdRouteComponent";
import {
  cartStore,
  updateSelectedItems,
} from "../CustomCartIdRouteComponent/storeCart";
import { TextFieldSimple } from "../MuiWrappers/TextFieldSimple";

import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useEffectEvent,
  useState,
} from "react";
import { JSX } from "react";

//import { isLineItem, isSubItem } from "../Table/helper/helper";

import {
  IconButtonLink,
  StandardComponents,
  VMItemGroups,
  VMLineItems,
  VMQuotes,
} from "@4pace-cpq/core";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Checkbox,
  TableCell,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSearch } from "@tanstack/react-router";
import { useStore } from "@tanstack/react-store";

import { theme as CustomLineItemTheme } from "./CustomLineItemTheme";

export interface ISyncSelect {
  configId: string;
  checked: boolean;
}
interface ICustomLineItem {
  item: VMLineItems;
  quoteId: VMQuotes["id"];
  itemGroupId: VMItemGroups["id"];
  idx: number;
  error?: ErrorType;
  errorList?: ErrorType[];
}

export function CustomLineItem({
  item,
  quoteId,
  itemGroupId,
  idx,
  error,
  errorList,
}: ICustomLineItem): JSX.Element {
  const t = useUiTranslator();
  const { isLoggedIntoEasy } = useEasySession();
  const { data: lineItemProperties } = useGetApiLineItemApiLineItemProperties({
    lineItemId: item.id,
  });
  const [notes, setNotes] = useState<string>(
    lineItemProperties?.data.notes || "",
  );
  const [customerId, setCustomerId] = useState<string>(
    lineItemProperties?.data.customerId || "",
  );
  const checked = useStore(cartStore, (state) =>
    state.selectedItems.includes(item.id),
  );
  const { data: administrationAreaData, isFetching } =
    useGetApiEasyAdministrationArea();

  const {
    mutate: setLineItemProperties,
    isPending: isPendingPutLineItemProps,
  } = usePutApiLineItemApiLineItemPropertiesWithJson();

  const { productCategory } = useSearch({ strict: false });

  const search = {
    quoteId,
    itemGroupId,
    lineItemId: item.id,
    cfgObjectId: item.product?.cfgObjectIdentifier,
    configurationId: item.configurationId,
    productCategory,
    administrationArea: administrationAreaData?.data.string,
    notes,
    customerId,
    systemEnvironment: import.meta.env.MODE,
  };

  const handleSyncSelect = useCallback((): void => {
    updateSelectedItems(item.id);
  }, [item.id]);

  const handleLineItemPropertiesChange = useEffectEvent(() => {
    if (lineItemProperties?.data.notes) {
      setNotes(lineItemProperties?.data.notes);
    }
    if (lineItemProperties?.data.customerId) {
      setCustomerId(lineItemProperties?.data.customerId);
    }
  });

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleLineItemPropertiesChange();
  }, [lineItemProperties]);

  const sendNotesChange = useCallback(
    (value: string) => {
      setLineItemProperties({
        params: { lineItemId: item.id },
        data: {
          notes: value,
        },
      });
    },
    [item.id, setLineItemProperties],
  );

  const sendCustomerIdChange = useCallback(
    (value: string) => {
      setLineItemProperties({
        params: { lineItemId: item.id },
        data: {
          customerId: value,
        },
      });
    },
    [item.id, setLineItemProperties],
  );

  /*   const debouncedNotesChange = useMemo(() => {
    return debounce(sendNotesChange, 500);
  }, [sendNotesChange]); */

  /*   const debouncedCustomerIdChange = useMemo(() => {
    return debounce(sendCustomerIdChange, 1000);
  }, [sendCustomerIdChange]); */

  function handleNotesChange(value: string): void {
    setNotes(value);
  }

  function handleCustomerIdChange(value: string): void {
    setCustomerId(value);
  }

  const handleNotesBlur = (value?: string): void => {
    sendNotesChange(value || notes);
  };

  const handleNotesKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    handleNotesBlur();
    event.currentTarget.blur();
  };

  const handleCustomerIdBlur = (value?: string): void => {
    sendCustomerIdChange(value || customerId);
  };

  const handleCustomerIdKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
  ): void => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    handleCustomerIdBlur();
    event.currentTarget.blur();
  };

  return (
    <ThemeProvider theme={CustomLineItemTheme}>
      {errorList && errorList.length > 0 && (
        <TableCell component="th" scope="row" align="right">
          {error?.exceptionMessage && error?.exceptionMessage !== "" && (
            <Tooltip title={error?.exceptionMessage}>
              <WarningIcon color="warning" />
            </Tooltip>
          )}
        </TableCell>
      )}
      {isLoggedIntoEasy && (
        <TableCell component="th" scope="row" align="right">
          {<Checkbox checked={checked} onChange={handleSyncSelect} />}
        </TableCell>
      )}
      <TableCell component="th" scope="row" align="center">
        {idx + 1}
      </TableCell>
      <TableCell align="left">
        <Box display="flex" alignItems="center">
          <Tooltip title={item.id}>
            <Typography variant="body2" noWrap maxWidth={"4em"}>
              {item.id}
            </Typography>
          </Tooltip>
          <CopyToClipboard data={item.id?.toString()} />
        </Box>
      </TableCell>
      <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
        {lineItemProperties?.data.typeCode}
      </TableCell>
      <TableCell
        align="center"
        sx={{ ">.MuiFormControl-root": { width: "4rem" } }}
      >
        <StandardComponents.ConnectedQuantity
          item={item}
          value={item.quantity || 1}
          allowDecimal={false}
        />
      </TableCell>
      <TableCell align="left">
        <TextFieldSimple
          value={notes}
          disabled={isPendingPutLineItemProps}
          //  multiline
          onBlur={handleNotesBlur}
          onKeyDown={handleNotesKeyDown}
          onChange={(e) => {
            handleNotesChange(e.target.value);
          }}
        />
      </TableCell>
      <TableCell align="left">
        <TextFieldSimple
          value={customerId}
          disabled={isPendingPutLineItemProps}
          onBlur={handleCustomerIdBlur}
          onKeyDown={handleCustomerIdKeyDown}
          onChange={(e) => {
            handleCustomerIdChange(e.target.value);
          }}
          sx={{ maxWidth: "6rem" }}
        />
      </TableCell>
      {/*    <TableCell align="left">
        <PriceIndicator
          price={Number(lineItemProperties?.data.priceIndicator)}
        />
      </TableCell> */}
      {/* {isLoggedIntoEasy && <TableCell align="left">Completed</TableCell>} */}
      <TableCell align="right">
        <Box display="flex">
          <Tooltip title={t("CustomApp:buttons.edit", "Edit")}>
            <IconButtonLink
              to="/configurator"
              search={search}
              disabled={isFetching}
            >
              <EditIcon
                color={item.configuration?.isComplete ? "success" : "warning"}
              />
            </IconButtonLink>
          </Tooltip>
          <StandardComponents.CloneItemIconButton item={item} />
          <StandardComponents.DeleteItemIconButton item={item} />
        </Box>
      </TableCell>
    </ThemeProvider>
  );
}
